import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    const [permissions, setPermissions] = useState({});
    const [isAdminFlag, setIsAdminFlag] = useState(false);

    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            (async () => {
                await fetchUser();
                await fetchPermissions();
                setLoading(false);
            })();
        } else {
            setLoading(false);
        }
    }, [token]);

    const fetchUser = async () => {
        try {
            const response = await axios.get(`${API}/auth/me`);
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            logout();
        }
    };

    const fetchPermissions = async () => {
        try {
            const { data } = await axios.get(`${API}/me/permissions`);
            setPermissions(data.permissions || {});
            setIsAdminFlag(!!data.is_admin);
        } catch (error) {
            console.error('Failed to fetch permissions:', error);
            setPermissions({});
            setIsAdminFlag(false);
        }
    };

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(userData);
        // refresh permissions for new session
        try {
            const { data } = await axios.get(`${API}/me/permissions`);
            setPermissions(data.permissions || {});
            setIsAdminFlag(!!data.is_admin);
        } catch (e) {
            setPermissions({});
            setIsAdminFlag(false);
        }
        return userData;
    };

    const logout = () => {
        // Clear session-scoped location prompt so next login re-asks
        if (user?.id) sessionStorage.removeItem(`location_selected_${user.id}`);
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
        setPermissions({});
        setIsAdminFlag(false);
    };

    const isAdmin = () => isAdminFlag || user?.role === 'admin';
    const isManager = () => user?.role === 'manager';
    const isSeller = () => user?.role === 'seller';
    const canManageUsers = () => isAdmin() || isManager();

    /**
     * Granular permission check.
     * Usage:
     *   can('pos.access')          → checks "pos" module has "access" action
     *   can('pos', 'access')       → same
     *   canAny('sales.read_all', 'sales.read_own')  → returns true if any matches
     */
    const can = (a, b) => {
        if (isAdmin()) return true;
        let module_, action_;
        if (b === undefined) {
            const parts = (a || '').split('.');
            if (parts.length !== 2) return false;
            [module_, action_] = parts;
        } else {
            module_ = a;
            action_ = b;
        }
        const actions = permissions[module_] || [];
        return actions.includes(action_);
    };

    const canAny = (...perms) => perms.some(p => can(p));

    return (
        <AuthContext.Provider value={{
            user,
            token,
            loading,
            permissions,
            login,
            logout,
            isAdmin,
            isManager,
            isSeller,
            canManageUsers,
            fetchUser,
            fetchPermissions,
            can,
            canAny
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
